// ⚡ VERSION 3.0.0 - EDITABLE ROLE DROPDOWN FIXED - CACHE BUSTER ⚡
// If you see this comment in browser console, the new code is loaded!
console.log('🚀 UsersTable v3.0.0 with Editable Role Dropdown FIXED loaded!');

import { Building2, Eye, Loader2, MoreVertical, Plug } from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ROLE_LABELS, type User, type UserRole } from '@/types/user';

interface UsersTableProps {
  users: User[];
  isLoading?: boolean;
  onViewUser: (user: User) => void;
  onManageIntegrations: (user: User) => void;
  onManageCompanies: (user: User) => void;
  onUpdateUserRole?: (userId: string, newRole: UserRole) => Promise<void>;
  currentUserId?: string;
  canManageUsers?: boolean;
}

export function UsersTable({
  users,
  isLoading = false,
  onViewUser,
  onManageIntegrations,
  onManageCompanies,
  onUpdateUserRole,
  currentUserId,
  canManageUsers = false,
}: UsersTableProps) {
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);

  // Debug logging
  console.log('UsersTable props:', {
    onUpdateUserRole: !!onUpdateUserRole,
    canManageUsers,
    currentUserId,
    usersCount: users.length,
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'default';
      case 'user':
        return 'secondary';

      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default' as const;
      case 'suspended':
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'suspended':
        return 'Suspended';
      default:
        return 'Inactive';
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (!onUpdateUserRole) return;

    setUpdatingRoleUserId(userId);
    try {
      await onUpdateUserRole(userId, newRole);
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const isRoleEditable = (user: User) => {
    // Can't edit if no update handler provided
    if (!onUpdateUserRole) {
      console.log('isRoleEditable: No update handler provided');
      return false;
    }

    // Can't edit if user doesn't have permission
    if (!canManageUsers) {
      console.log('isRoleEditable: User does not have permission to manage users');
      return false;
    }

    // Can't edit own role (if currentUserId is provided)
    if (currentUserId && user.id === currentUserId) {
      console.log('isRoleEditable: Cannot edit own role');
      return false;
    }

    console.log('isRoleEditable: Role is editable for user', user.email);
    return true;
  };

  const getIntegrationStatusBadge = (user: User) => {
    const isConnected = !!user.evoUserId;

    if (isConnected) {
      return (
        <Badge
          className="bg-green-500/10 text-green-500 hover:bg-green-500/20 cursor-pointer transition-colors"
          onClick={() => onManageIntegrations(user)}
        >
          <Plug className="h-3 w-3 mr-1" />
          Connected
        </Badge>
      );
    }

    return (
      <Badge
        variant="outline"
        className="cursor-pointer hover:bg-accent transition-colors"
        onClick={() => onManageIntegrations(user)}
      >
        <Plug className="h-3 w-3 mr-1" />
        Not Connected
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg p-12 text-center">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading users...</span>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="border rounded-lg p-12 text-center">
        <p className="text-muted-foreground">No users found</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Companies</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            return (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                      <AvatarFallback>{getInitials(user.firstName, user.lastName)}</AvatarFallback>
                    </Avatar>
                    <div className="font-medium">
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {isRoleEditable(user) ? (
                    updatingRoleUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span className="text-sm">Updating...</span>
                      </div>
                    ) : (
                      <Select value={user.role} onValueChange={(value) => handleRoleChange(user.id, value as UserRole)}>
                        <SelectTrigger className="w-[160px] h-8">
                          <SelectValue>
                            <Badge variant={getRoleBadgeVariant(user.role)} className="pointer-events-none">
                              {ROLE_LABELS[user.role]}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="administrator">
                            <Badge variant="default" className="pointer-events-none">
                              {ROLE_LABELS.administrator}
                            </Badge>
                          </SelectItem>
                          <SelectItem value="user">
                            <Badge variant="secondary" className="pointer-events-none">
                              {ROLE_LABELS.user}
                            </Badge>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )
                  ) : (
                    <Badge variant={getRoleBadgeVariant(user.role)}>{ROLE_LABELS[user.role]}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={getStatusBadgeVariant(user.status)}
                    className={
                      user.status === 'suspended' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' : undefined
                    }
                  >
                    {getStatusLabel(user.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.companyCount > 0 ? (
                    <span className="text-sm font-medium">
                      {user.companyCount} {user.companyCount === 1 ? 'company' : 'companies'}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewUser(user)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onManageCompanies(user)}>
                        <Building2 className="w-4 h-4 mr-2" />
                        Manage Companies
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
